import * as React from 'react'

import { Radio } from '@factorialco/deprecated-design-system'
import { Form as FactorialForm } from 'factorial-form'
import { reaction } from 'mobx'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import AccessGroup from 'modules/core/store/models/AccessGroup'

import { useEngagementContext } from 'modules/engagement/hooks/useEngagementContext'
import type AccessGroupForm from 'modules/surveys/components/AccessGroupForm'
import CompanyGroupSelection from 'modules/surveys/components/CompanyGroupSelection'
import DynamicGroupSelection from 'modules/surveys/components/DynamicGroupSelection'

import ManualSelection from './ManualSelection'

const FieldName = 'respondent'

const AccessGroupOptions = {
  company: 'company',
  dynamicGroup: 'dynamicGroup',
  manualSelection: 'manualSelection',
}
interface Props {
  form: AccessGroupForm
}

function AccessGroupPicker({ form: accessGroupForm }: Props) {
  const { allMyDescendants } = useCurrentSession()
  const { canCreateEverybody } = useEngagementContext()

  const form = React.useRef(
    new FactorialForm(
      {
        group: null,
      },
      {
        group: 'string',
      }
    )
  ).current

  const field = React.useRef(form.get('group')).current

  const setMembers = React.useRef(accessGroupForm.setMembers.bind(accessGroupForm)).current

  React.useEffect(() => {
    const handler = reaction(
      () => field.value,
      (value: string) => {
        if (value !== AccessGroupOptions.manualSelection) return

        accessGroupForm.get('strategy').set(AccessGroup.Strategies.members)
      }
    )

    return () => handler()
  }, [accessGroupForm, form, field])

  return (
    <>
      {canCreateEverybody && (
        <>
          <Radio.Wrap>
            <Radio
              name={FieldName}
              value={field.value}
              onChange={(value) => field.set(value)}
              description={i18n.t('surveys.new.form.labels.employees.description')}
              option={{
                label: i18n.t('surveys.new.form.labels.employees.title'),
                value: AccessGroupOptions.company,
              }}
            >
              <CompanyGroupSelection field={accessGroupForm.get('strategy')} />
            </Radio>
          </Radio.Wrap>
          <Radio.Wrap>
            <Radio
              name={FieldName}
              value={field.value}
              onChange={(value) => field.set(value)}
              description={i18n.t('surveys.new.form.labels.dynamic_group.description')}
              option={{
                label: i18n.t('surveys.new.form.labels.dynamic_group.title'),
                value: AccessGroupOptions.dynamicGroup,
              }}
            >
              <DynamicGroupSelection
                groupField={accessGroupForm.get('groupable_type')}
                groupableIdField={accessGroupForm.get('groupable_id')}
              />
            </Radio>
          </Radio.Wrap>
        </>
      )}
      <Radio.Wrap>
        <Radio
          name={FieldName}
          value={field.value}
          onChange={(value) => field.set(value)}
          description={i18n.t('surveys.new.form.labels.manual_selection.description')}
          option={{
            label: i18n.t('surveys.new.form.labels.manual_selection.title'),
            value: AccessGroupOptions.manualSelection,
          }}
        >
          <ManualSelection
            members={accessGroupForm.members}
            setMembers={setMembers}
            allMyDescendants={allMyDescendants}
          />
        </Radio>
      </Radio.Wrap>
    </>
  )
}

export default observer(AccessGroupPicker)
